import React from "react";
import moment from "moment-timezone";
import { Box } from "theme-ui";

import { mnt, mntPill } from "theme/maintenanceTokens";

/**
 * Shared Work Order presentation layer.
 *
 * WHY THIS EXISTS: the same WO row logic had been copy-pasted into three
 * surfaces (the global queue `WorkOrderReviewQueue`, the plan-scoped
 * `PlanWorkOrdersSection`, and the WO case file `WorkOrderDetail`), each
 * carrying its own `lifecycleBadge` / `slaCell` / `receivedCell` /
 * `formatNte` / `sourceLabel`. The duplication was flagged as deliberate
 * at the time ("a shared extraction is a later no-behavior cleanup",
 * PlanWorkOrdersSection Session 48) on the promise that a WO would read
 * identically everywhere. It stopped being true: the three copies drifted
 * on placeholder glyphs, aging colors, and which of them consumed the
 * derived `effectiveStage`. Session 55 extracts them so a WO genuinely
 * reads the same in every surface, and so the Plan Detail scale rebuild
 * has one place to change rather than three.
 *
 * SCOPE: presentation only. No fetching, no mutations, no state. Every
 * function here is pure given a WO row; the components are stateless.
 * Colors come from `theme/maintenanceTokens` so the mockup palette is
 * applied by construction (MOCKUP_FIDELITY_PASS.md).
 *
 * The stage vocabulary is the backend's derived `effectiveStage`
 * (WO_STATUS_DERIVATION.md), never a hand-set status. The raw
 * status/matchStatus fallbacks below exist only for the window where a
 * surface reads a payload from a backend that has not been deployed with
 * the derivation yet; they are not a second source of truth.
 */

/* ─── source ──────────────────────────────────────────────────────── */

export function isCorrigoSource(sourceType?: string): boolean {
  if (!sourceType) return false;
  const lc = sourceType.toLowerCase();
  return lc.includes("corrigo") || lc.includes("email");
}

export function sourceLabel(sourceType?: string): string {
  if (!sourceType) return "-";
  // Item 18 — checked BEFORE isCorrigoSource, which matches on the
  // substring "email" and would otherwise be reached first by any
  // future email-ish member. A client-raised work order reads "Client
  // request" rather than the title-cased enum, because what the operator
  // needs to know is WHO asked, not which surface it arrived through.
  if (sourceType === "client_portal") return "Client request";
  if (isCorrigoSource(sourceType)) return "CorrigoPro";
  if (sourceType === "manual_upload") return "Manual";
  if (sourceType === "scheduled") return "Recurring";
  // Fallback: title-case the raw enum.
  return sourceType
    .split("_")
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(" ");
}

/**
 * Mockup 06 draws the source as a chip, not plain text: CorrigoPro in
 * soft blue with the link ink, everything else in the neutral grey pair.
 * The chip is what lets a reviewer see at a glance which rows came from
 * the client's system versus ones we raised ourselves.
 */
export function SourcePill({ sourceType }: { sourceType?: string }) {
  const corrigo = isCorrigoSource(sourceType);
  return (
    <Box
      as="span"
      sx={{
        display: "inline-block",
        background: corrigo ? mnt.corrigoPill : mnt.greyPill,
        color: corrigo ? mnt.corrigoPillFg : mnt.grey,
        borderRadius: "9px",
        padding: "2px 9px",
        fontSize: "10.5px",
        fontFamily: "Poppins",
        fontWeight: 500,
        whiteSpace: "nowrap",
      }}
    >
      {sourceLabel(sourceType)}
    </Box>
  );
}

/* ─── identity (number vs name) ───────────────────────────────────── */

/**
 * A12 (Session 57) — how a work order names itself, in ONE place so the
 * queue row, the case-file header, and the trunk context banner all read
 * it identically.
 *
 * A WO carries two independent identity fields:
 *   - `externalId` — the client's WORK ORDER NUMBER ("374295-..."). The
 *     identifier: what EMCOR's people search and reference by. On a
 *     work_order_fed plan this is the primary handle.
 *   - `name` — an optional human DESCRIPTOR ("Mulch install, north
 *     playground"). What the job IS, as distinct from which job it is.
 *
 * Precedence (Jack, Session 57): the NUMBER leads when present; the name
 * is then the secondary descriptor line. When there is no number, the
 * name becomes the primary identity. A WO is guaranteed (by the create /
 * update guards on work_order_fed plans) to have at least one of the two,
 * so `primary` is only ever empty for a legacy/quote-fed WO that has
 * neither — handled with a last-resort fallback so no surface renders a
 * blank.
 *
 * Returns `{ primary, secondary }`:
 *   - primary   — the bold handle a surface leads with.
 *   - secondary — the descriptor line, present ONLY when it adds
 *     something the primary does not already say (so a name-only WO does
 *     not echo the same text on two lines). Null when there is nothing
 *     extra to show.
 */
export function workOrderDisplayIdentity(wo: any): {
  primary: string;
  secondary: string | null;
} {
  const number = (wo?.externalId ?? "").trim();
  const name = (wo?.name ?? "").trim();

  if (number) {
    const secondary = name && name !== number ? name : null;
    return { primary: number, secondary };
  }

  if (name) {
    return { primary: name, secondary: null };
  }

  // Neither present (legacy / quote-fed WO with no identity set). Never
  // render blank: fall back to the problem description, then a short id
  // stub, so a row is always addressable.
  const problem = (wo?.problemDescription ?? "").trim();
  if (problem) {
    return {
      primary: problem.length > 60 ? `${problem.slice(0, 60)}\u2026` : problem,
      secondary: null,
    };
  }
  const idStub = wo?.id ? `WO ${String(wo.id).slice(0, 8)}` : "Work order";
  return { primary: idStub, secondary: null };
}

/* ─── stage / lifecycle badge ─────────────────────────────────────── */

export type BadgeVariant = "danger" | "warning" | "success" | "info";

const STAGE_BADGE: Record<string, { variant: BadgeVariant; label: string }> = {
  intake: { variant: "warning", label: "Intake" },
  rfq_generated: { variant: "info", label: "RFQ generated" },
  quote_sent: { variant: "info", label: "Quote sent" },
  accepted: { variant: "success", label: "Accepted" },
  in_production: { variant: "success", label: "In production" },
  invoiced: { variant: "success", label: "Invoiced" },
  declined: { variant: "info", label: "Declined" },
  legacy_closed: { variant: "info", label: "Cancelled" },
};

/**
 * The badge a WO row shows. Prefers the backend-derived
 * `effectiveStage` so a post-RFQ WO reads its true rung (RFQ generated /
 * Quote sent / Accepted / In production / Invoiced) instead of a flat
 * "In progress".
 *
 * `intake` deliberately falls THROUGH to the match-aware logic below:
 * at intake the useful distinction is "needs match" vs "needs RFQ",
 * which the derived stage does not carry.
 *
 * OPERATING_MODEL 6.5: `status='converted'` is a FORWARD-MOVING
 * "RFQ generated, case in progress" state, not a terminal one. Only the
 * legacy `rejected` value is genuinely closed.
 */
export function lifecycleBadge(wo: any): {
  variant: BadgeVariant;
  label: string;
  hint: string | null;
} {
  const stage = wo?.effectiveStage as string | undefined;
  // Quoted, but the quote died (2026-10-16): the rung is still
  // rfq_generated (it needs a re-quote) - say so instead of a bare
  // "RFQ generated" that reads as never quoted.
  if (stage === "rfq_generated" && /^Quote (expired|superseded)/i.test(String(wo?.stageHint ?? ""))) {
    return {
      variant: "warning",
      label: /^Quote expired/i.test(String(wo.stageHint)) ? "Quote expired" : "Re-quote needed",
      hint: wo.stageHint,
    };
  }
  if (stage && stage !== "intake" && STAGE_BADGE[stage]) {
    const s = STAGE_BADGE[stage];
    return { variant: s.variant, label: s.label, hint: wo?.stageHint ?? null };
  }

  if (wo?.status === "converted") {
    return { variant: "info", label: "In progress", hint: "RFQ generated" };
  }
  if (wo?.status === "rejected") {
    // 'rejected' is the shared terminal for a cancelled WO and a rejected
    // intake row; the product treats both as cancelled (the WO detail
    // header labels it "Cancelled" too), so the badge matches.
    return { variant: "info", label: "Cancelled", hint: null };
  }
  if (wo?.matchStatus === "unmatched") {
    return { variant: "danger", label: "Needs match", hint: null };
  }
  const hint =
    wo?.matchStatus === "manual_matched"
      ? "matched manually"
      : wo?.matchStatus === "matched"
      ? "auto-matched"
      : null;
  return { variant: "warning", label: "Needs RFQ", hint };
}

/* ─── lifecycle predicates (one definition of "what can I do here") ── */

/** Post-RFQ (in progress) or closed. No WO-level match or generate-RFQ
 * action applies in either case, and the intake cues are suppressed. */
export function isPostRfq(wo: any): boolean {
  return wo?.status === "converted" || wo?.status === "rejected";
}

export function canMatchWo(wo: any): boolean {
  return wo?.matchStatus === "unmatched" && !isPostRfq(wo);
}

export function canGenerateRfqForWo(wo: any): boolean {
  return ["matched", "manual_matched"].includes(wo?.matchStatus) && !isPostRfq(wo);
}

/**
 * Why a WO has not resolved to a plan-managed site. Null once the RFQ is
 * generated (the cue is intake guidance, not a permanent property) and
 * null when everything resolved. `requirePlan` is false on plan-scoped
 * surfaces, where "site not in any plan" cannot happen by construction.
 */
export function unresolvedReason(wo: any, requirePlan = true): string | null {
  if (isPostRfq(wo)) return null;
  if (!wo?.lcp?.id) {
    // The matcher DID find the site but would not auto-pick because more
    // than one location scored as a match (almost always a duplicate
    // location record for the same address). Saying "No matching site"
    // there sent staff hunting for a site that already exists.
    const t = wo?.sourceMetadata?.matchTelemetry;
    const n = Number(t?.candidateCount ?? 0);
    if (t?.status === "prompt" && n > 1) return n + " possible sites, pick one";
    return "No matching site";
  }
  if (requirePlan && !wo?.servicePlan?.name) return "Site not in any plan";
  return null;
}

/* ─── time + money cells ──────────────────────────────────────────── */

export interface CellStyle {
  label: string;
  color: string;
  weight: number;
}

/**
 * SLA cell: the complete-by date with urgency. Only closed (legacy
 * `rejected`) WOs render neutral. A converted WO is an in-progress case
 * and keeps its urgency coloring, because the SLA clock does not stop
 * when the RFQ is generated.
 */
export function slaCell(wo: any): CellStyle | null {
  if (!wo?.completeByAt) return null;
  const due = moment(wo.completeByAt);
  const days = due.diff(moment(), "days");
  const isClosed = wo?.status === "rejected";

  let label: string;
  if (days < 0) label = `${Math.abs(days)}d overdue`;
  else if (days === 0) label = "due today";
  else label = `comp by ${due.format("MMM D")}`;

  if (isClosed) return { label, color: mnt.textMuted, weight: 400 };
  if (days < 0) return { label, color: mnt.red, weight: 600 };
  if (days <= 3) return { label, color: mnt.amber, weight: 600 };
  return { label, color: mnt.textMuted, weight: 400 };
}

/** Received cell: relative age, with an aging cue while the WO is still
 * pre-RFQ. Once the case is moving (converted) or closed the cue is
 * dropped: staff are no longer waiting on intake triage. */
export function receivedCell(wo: any): CellStyle {
  if (!wo?.receivedAt) return { label: "-", color: mnt.textMuted, weight: 400 };
  const receivedAt = moment(wo.receivedAt);
  const now = moment();
  const days = now.diff(receivedAt, "days");
  const hours = now.diff(receivedAt, "hours");
  const minutes = now.diff(receivedAt, "minutes");

  let label: string;
  if (minutes < 60) label = `${Math.max(minutes, 1)}m ago`;
  else if (hours < 24) label = `${hours}h ago`;
  else if (days < 7) label = `${days}d ago`;
  else label = receivedAt.format("MMM D");

  if (isPostRfq(wo)) return { label, color: mnt.textMuted, weight: 400 };
  if (days >= 5) return { label, color: mnt.red, weight: 600 };
  if (days >= 3) return { label, color: mnt.amber, weight: 600 };
  return { label, color: mnt.textMuted, weight: 400 };
}

export function formatNte(nte: any): string {
  if (nte == null || nte === "") return "-";
  const n = Number(nte);
  if (!Number.isFinite(n) || n === 0) return "-";
  return "$" + n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

/* ─── small pills ─────────────────────────────────────────────────── */

/** Mockup 06: the unresolved cue is an amber pill under the site, not
 * red body text. It reads as guidance ("this needs matching") rather
 * than as an error. */
export function UnresolvedPill({ reason }: { reason: string }) {
  return (
    <Box sx={{ ...mntPill("due"), color: mnt.amberDeep, fontSize: "10.5px" }}>{reason}</Box>
  );
}

export function UrgentPill() {
  return <Box sx={{ ...mntPill("urgent"), fontSize: "10px" }}>Urgent</Box>;
}

/** Neutral chip used for the "One-off" marker (a WO on a known site that
 * belongs to no plan). */
export function OneOffPill() {
  return (
    <Box
      as="span"
      sx={{
        display: "inline-block",
        background: mnt.greyPill,
        borderRadius: "9px",
        padding: "2px 8px",
        fontSize: "10.5px",
        fontFamily: "Poppins",
        color: mnt.grey,
      }}
    >
      One-off
    </Box>
  );
}

/** Whether a WO has a measured mulch map attached (priced-from). The list
 * queries join `wo.mulchMap` (Session 68), so presence of that object — or
 * a bare `mulchMapId` — means the site has been measured for this WO. */
export function isWorkOrderMapped(wo: any): boolean {
  return Boolean(wo?.mulchMap || wo?.mulchMapId);
}

/** Whether the map indicator applies to this WO at all. KNOWN_ISSUES #11:
 * the callers used to suppress it on `isPostRfq`, which is BOTH converted
 * and rejected. Every manually created WO is converted at creation, so the
 * indicator never rendered on one in either direction — on the LCG plan,
 * 6 manual WOs carrying a linked map showed no "Mapped" chip and 4 priced
 * with no map at all showed no cue either. It was never conditioned on the
 * source, as the register guessed; it was conditioned on the stage.
 * Rejected work is dead, so a map cue there is genuine noise. Everything
 * else shows it. */
export function showsMapStatus(wo: any): boolean {
  return wo?.status !== "rejected";
}

/** The map chip. For work-order-fed intake the quantity is unknown until
 * someone measures the site, so pre-pricing this tells an operator which
 * rows still need a map before they can be priced — an ACTION, drawn amber.
 *
 * Past the RFQ the price is already set, so the same absence stops being a
 * task and becomes a fact about how the work was priced. It still matters
 * (an RFQ raised with no measurement is an unverified quantity) but nobody
 * should be chasing it, so it reads neutral grey and says "No map" rather
 * than prompting for one. A map that IS attached reads "Mapped" at every
 * stage — post-RFQ that is the artifact the price came from. */
export function MapStatusPill({ wo }: { wo: any }) {
  const mapped = isWorkOrderMapped(wo);
  const priced = isPostRfq(wo);
  const title = mapped
    ? priced
      ? "Priced from an attached map"
      : "A map is attached — ready to price"
    : priced
    ? "Priced without a site measurement"
    : "No map yet — measure the site to price this";
  return (
    <Box
      as="span"
      title={title}
      sx={{
        display: "inline-block",
        background: mapped ? mnt.greenPill : priced ? mnt.greyPill : mnt.amberPill,
        borderRadius: "9px",
        padding: "2px 8px",
        fontSize: "10.5px",
        fontFamily: "Poppins",
        color: mapped ? mnt.greenFg : priced ? mnt.grey : mnt.amberDeep,
        whiteSpace: "nowrap",
      }}
    >
      {mapped ? "Mapped" : priced ? "No map" : "Needs map"}
    </Box>
  );
}
